import React, { useState } from 'react';
import { Calendar } from 'lucide-react';

interface DateRangeFilterProps {
  startDate?: Date;
  endDate?: Date;
  onChange: (start: Date, end: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  presets?: {
    label: string;
    getValue: () => { start: Date; end: Date };
  }[];
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  endDate = new Date(),
  onChange,
  minDate,
  maxDate,
  presets = [
    {
      label: 'Last 24 hours',
      getValue: () => ({
        start: new Date(Date.now() - 24 * 60 * 60 * 1000),
        end: new Date()
      })
    },
    {
      label: 'Last 7 days',
      getValue: () => ({
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        end: new Date()
      })
    },
    {
      label: 'Last 30 days',
      getValue: () => ({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        end: new Date()
      })
    },
    {
      label: 'Last 90 days',
      getValue: () => ({
        start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        end: new Date()
      })
    }
  ]
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('All historical');

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const handlePresetClick = (preset: typeof presets[0]) => {
    const { start, end } = preset.getValue();
    // Clamp to min/max bounds
    const clampedStart = minDate && start < minDate ? minDate : start;
    const clampedEnd = maxDate && end > maxDate ? maxDate : end;
    onChange(clampedStart, clampedEnd);
    setSelectedPreset(preset.label);
    setIsOpen(false);
  };

  const handleStartDateChange = (newStart: Date) => {
    // Ensure start date is within bounds and not after end date
    let clampedStart = newStart;
    if (minDate && newStart < minDate) clampedStart = minDate;
    if (maxDate && newStart > maxDate) clampedStart = maxDate;
    if (clampedStart > endDate) clampedStart = endDate;
    onChange(clampedStart, endDate);
  };

  const handleEndDateChange = (newEnd: Date) => {
    // Ensure end date is within bounds and not before start date
    let clampedEnd = newEnd;
    if (minDate && newEnd < minDate) clampedEnd = minDate;
    if (maxDate && newEnd > maxDate) clampedEnd = maxDate;
    if (clampedEnd < startDate) clampedEnd = startDate;
    onChange(startDate, clampedEnd);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800/95 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <Calendar className="w-4 h-4" />
        <span>{formatDate(startDate)} - {formatDate(endDate)}</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800/95 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-20">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Quick Filters
              </h4>
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                    selectedPreset === preset.label
                      ? 'bg-primary text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">From</label>
                  <input
                    type="month"
                    value={`${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`}
                    min={minDate ? `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}` : undefined}
                    max={maxDate ? `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}` : undefined}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-').map(Number);
                      handleStartDateChange(new Date(year, month - 1, 1));
                    }}
                    className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">To</label>
                  <input
                    type="month"
                    value={`${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`}
                    min={minDate ? `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}` : undefined}
                    max={maxDate ? `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}` : undefined}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-').map(Number);
                      // Set to last day of the month
                      handleEndDateChange(new Date(year, month, 0));
                    }}
                    className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangeFilter;